import { _ } from "libs";

import React from "react";
import moment from "moment";
import secondsToDaysHoursMinutes from "../../actions/secondsToDaysHoursMinutes";
import Avatar from "../avatar";
import ClubItemMedium from "../items/club/medium";
import { Stat } from "../../types";
import { RadialChart } from "react-vis";
import "react-vis/dist/style.css";
import { Link } from "react-router-dom";
import { openPlayerPopup } from "../../actions";

const STAT_TIME_SPENT = "time_spent",
	STAT_TIMES = "times",
	STAT_SPORTS = "sports",
	STAT_TYPE = "type",
	STAT_SURFACE = "surface",
	STAT_GAME_TYPE = "game_type",
	STAT_PARTNERS = "partners",
	STAT_CLUBS = "clubs",
	STAT_MATCHES = "matches",
	STAT_SETS = "sets";

function compareDesc(a, b) {
	if (a.absolute < b.absolute) {
		return 1;
	}
	if (a.absolute > b.absolute) {
		return -1;
	}
	return 0;
}

function findStatInList(list, name): Stat {
	const emptyStat = {
		references: [],
		simple: [],
	};
	if (!Array.isArray(list) || list.length === 0) {
		return emptyStat as Stat;
	}

	const stat = list.find(e => {
		if (e && e.hasOwnProperty("name")) {
			return e.name === name;
		}
	});

	if (!stat) {
		return emptyStat as Stat;
	}

	return stat;
}

const ChartColumn = ({ stats, type, translationPrefix }: { stats: any; type: string; translationPrefix: string }) => {
	return (
		<div className="nfsColumn">
			<div className="nfsChart">
				<RadialChart data={stats.data} width={72} height={72} radius={32} innerRadius={22} padAngle={0.08} />
			</div>
			<div className="nfsLabels">
				{stats.data.map((item, index) => {
					return (
						<div className={`label ${item.className}`} key={index}>
							{Math.floor(item.relative)}% {_(translationPrefix + "." + item.name)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

const Stats = ({ stats, startTime, endTime }: { stats: Stat[]; startTime: number; endTime: number }) => {
	if (!stats) {
		return null;
	}

	let month = moment.unix(startTime).format("MMMM");
	let games = findStatInList(stats, STAT_TIMES);
	let timePlayed = findStatInList(stats, STAT_TIME_SPENT);
	let partners = findStatInList(stats, STAT_PARTNERS);
	let clubs = findStatInList(stats, STAT_CLUBS);
	let sports = findStatInList(stats, STAT_SPORTS);
	let types = findStatInList(stats, STAT_TYPE);
	let surfaces = findStatInList(stats, STAT_SURFACE);
	let gameTypes = findStatInList(stats, STAT_GAME_TYPE);
	let matches = findStatInList(stats, STAT_MATCHES);
	let sets = findStatInList(stats, STAT_SETS);

	if (games.simple[0].absolute == 0) {
		return (
			<div className="nBlock newsFeedMonthlyStats">
				<h5>
					{_("timeline.stats.month", null, {
						month: month,
					})}
				</h5>
				<div className="emptyView">
					<div className="image" />
					<div className="label">{_("stats.noReservations")}</div>
				</div>
			</div>
		);
	}

	partners.references = partners.references.sort(compareDesc);
	clubs.references = clubs.references.sort(compareDesc);
	sports.simple = sports.simple.sort(compareDesc);
	types.simple = types.simple.sort(compareDesc);
	surfaces.simple = surfaces.simple.sort(compareDesc);
	gameTypes.simple = gameTypes.simple.sort(compareDesc);
	matches.simple = matches.simple.sort(compareDesc);
	sets.simple = sets.simple.sort(compareDesc);

	sports.data = [];
	sports.simple.map(item => {
		sports.data.push({
			name: item.name,
			angle: item.absolute,
			relative: item.relative,
			className: "sport" + item.name.charAt(0).toUpperCase() + item.name.slice(1),
		});
	});

	types.data = [];
	types.simple.map(item => {
		types.data.push({
			name: item.name,
			angle: item.absolute,
			relative: item.relative,
			className: "type" + item.name.charAt(0).toUpperCase() + item.name.slice(1),
		});
	});

	surfaces.data = [];
	surfaces.simple.map(item => {
		surfaces.data.push({
			name: item.name,
			angle: item.absolute,
			relative: item.relative,
			className: "surface" + item.name.charAt(0).toUpperCase() + item.name.slice(1),
		});
	});

	gameTypes.data = [];
	gameTypes.simple.map(item => {
		gameTypes.data.push({
			name: item.name,
			angle: item.absolute,
			relative: item.relative,
			className: "gameType" + item.name.charAt(0).toUpperCase() + item.name.slice(1),
		});
	});

	matches.data = [];
	matches.simple.map(item => {
		matches.data.push({
			name: item.name,
			angle: item.absolute,
			relative: item.relative,
			className: "matches" + item.name.charAt(0).toUpperCase() + item.name.slice(1),
		});
	});

	sets.data = [];
	sets.simple.map(item => {
		sets.data.push({
			name: item.name,
			angle: item.absolute,
			relative: item.relative,
			className: "matches" + item.name.charAt(0).toUpperCase() + item.name.slice(1),
		});
	});

	return (
		<div className="nBlock newsFeedMonthlyStats">
			<h5>
				{_("timeline.stats.month", null, {
					month: month,
				})}
			</h5>
			<div className="stats">
				<div className="stat">
					<div className="value">{secondsToDaysHoursMinutes(timePlayed.simple[0].absolute)}</div>
					<div className="key">{_("stats.time_played")}</div>
				</div>
				<div className="stat">
					<div className="value">{games.simple[0].absolute}</div>
					<div className="key">{_("stats.games")}</div>
				</div>
			</div>
			<div className="nfsRow">
				<div className="nfsGroup charts">
					<ChartColumn stats={sports} translationPrefix="courtSport" type="sport" />
					<ChartColumn stats={surfaces} translationPrefix="courtSurface" type="surface" />
					<ChartColumn stats={types} translationPrefix="courtType" type="type" />
					<ChartColumn stats={gameTypes} translationPrefix="gameType" type="gameType" />
				</div>
			</div>
			<div className="nfsRow">
				<div className="nfsGroup">
					<h6>{_("stats.partners.heading")}</h6>
					<div className="table partners">
						{partners.references.length > 0 ? (
							partners.references.map((player, index) => {
								let width = (player.absolute / partners.references[0].absolute) * 100;
								if (width < 100) {
									width = width + (100 - width) / 5;
								}
								return (
									<div
										onClick={() => openPlayerPopup(player.reference.playerId)}
										className="cell clickable"
										key={index}
									>
										<div
											className="partner"
											style={{ width: width + "%" }}
											title={player.reference.fullName}
										>
											<Avatar photoUrl={player.reference.photoUrl} />
											<div className="name">{player.reference.fullName}</div>
											<div className="value">{player.absolute}</div>
										</div>
									</div>
								);
							})
						) : (
							<div>{_("stats.noPartners")}</div>
						)}
					</div>
				</div>
				<div className="nfsGroup">
					<h6>{_("stats.clubs.heading")}</h6>
					{clubs.references.length > 0 ? (
						<div className="table clubs">
							{clubs.references.map((club, index) => {
								return (
									<Link to={"club/" + club.reference.clubId} className="cell" key={index}>
										<ClubItemMedium
											name={club.reference.name}
											meta={club.absolute + " game" + (club.absolute > 1 ? "s" : "")}
											photoUrl={club.reference.photoUrl}
										/>
									</Link>
								);
							})}
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
};

export default Stats;
